import React from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FaMagnifyingGlass, FaUser, FaCircleInfo, FaMobile, FaMobileScreenButton } from "react-icons/fa6";
import { IoReceiptOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useAuth } from "shared/providers/auth";
import { searchPatients } from "requests/Patient";
import { IPatient } from "shared/types/Patient";
import { DEFAULT_AVATAR } from "shared/config/properties";
import { ReactComponent as MenuIcon } from "shared/assets/svg/menu.svg";
import { DentistHistoryModal } from "components/DentistHistoryModal";
import { ReferralStatusTracker } from "components/Referrals";
import { PatientInfoModal } from "components/PatientInfoModal";
import * as Styled from "./styles";

const PatientSearch: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [patients, setPatients] = React.useState<IPatient[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [hasSearched, setHasSearched] = React.useState<boolean>(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPatient, setSelectedPatient] = React.useState<IPatient | null>(null);
  const open = Boolean(anchorEl);

  const [openDentistHistory, setOpenDentistHistory] = React.useState<boolean>(false);
  const [openReferrals, setOpenReferrals] = React.useState<boolean>(false);
  const [openPatientInfo, setOpenPatientInfo] = React.useState<boolean>(false);

  // Gera tooltip de informações do app
  const getAppTooltip = (patient: IPatient): string => {
    if (patient.has_app && patient.app_info) {
      let tooltip = "✅ App instalado";
      if (patient.app_info.ultimo_acesso) {
        tooltip += `\n📅 Último acesso: ${patient.app_info.ultimo_acesso}`;
      }
      if (patient.app_info.responsavel) {
        tooltip += "\n👤 Cadastro de responsável";
      }
      return tooltip;
    }
    return "❌ App Cliente não instalado\n\nOriente o paciente a baixar o app para facilitar a comunicação e engajamento.";
  };

  // Gera tooltip de condições neurológicas
  const getNeuroTooltip = (patient: IPatient): string => {
    if (patient.has_neuro && patient.neuro_info) {
      let tooltip = "⚠️ Condições Neurológicas e Transtornos\n\n";
      
      if (patient.neuro_info.conditions && patient.neuro_info.conditions.length > 0) {
        tooltip += patient.neuro_info.conditions.map(c => `• ${c}`).join("\n");
      }
      
      if (patient.neuro_info.other_conditions) {
        tooltip += `\n\n📝 Outras: ${patient.neuro_info.other_conditions}`;
      }
      
      return tooltip;
    }
    return "";
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, patient: IPatient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  // Debounce para busca
  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 3) {
        handleSearch();
      } else if (searchTerm.length === 0) {
        setPatients([]);
        setHasSearched(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = async () => {
    if (searchTerm.length < 3) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const { data, status } = await searchPatients(user.data.id, searchTerm);

      if ([200, 201].includes(status)) {
        setPatients(data.data);
      } else {
        setPatients([]);
      }
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Styled.Container>
      <Styled.SearchBox>
        <h5>Buscar Pacientes</h5>
        <p>
          Busque por nome, CPF ou código do paciente para visualizar o prontuário.
        </p>

        <TextField
          placeholder="Digite o nome, CPF ou código do paciente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaMagnifyingGlass color="#666" />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
      </Styled.SearchBox>

      <Styled.InfoBadge>
        <FaCircleInfo size={18} />
        <span>
          Modo visualização: acesse documentação, ficha clínica e odontograma sem iniciar atendimento.
        </span>
      </Styled.InfoBadge>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : patients.length > 0 ? (
        <Styled.ResultsList>
          {patients.map((patient) => (
            <Styled.PatientCard key={patient.pac_id}>
              <Styled.PatientInfo>
                <Styled.AvatarContainer>
                  <Avatar
                    src={patient.pac_avatar || DEFAULT_AVATAR}
                    alt={patient.pac_name}
                    sx={{ width: 55, height: 55 }}
                  />
                  {patient.has_neuro && (
                    <Tooltip
                      title={
                        <span style={{ whiteSpace: "pre-line" }}>
                          {getNeuroTooltip(patient)}
                        </span>
                      }
                      arrow
                      placement="top"
                    >
                      <Styled.AccessibilityBadge>
                        <FaCircleInfo size={12} />
                      </Styled.AccessibilityBadge>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: "pre-line" }}>
                        {getAppTooltip(patient)}
                      </span>
                    }
                    arrow
                    placement="top"
                  >
                    <Styled.AppBadge $hasApp={patient.has_app}>
                      {patient.has_app ? (
                        <FaMobileScreenButton size={12} />
                      ) : (
                        <FaMobile size={12} />
                      )}
                    </Styled.AppBadge>
                  </Tooltip>
                </Styled.AvatarContainer>
                <Styled.PatientDetails>
                  <Styled.PatientId>ID {patient.pac_id_fant}</Styled.PatientId>
                  <Styled.PatientName>{patient.pac_name}</Styled.PatientName>
                  {patient.cpf && (
                    <Styled.PatientCpf>CPF: {patient.cpf}</Styled.PatientCpf>
                  )}
                  {patient.situation_label && (
                    <Styled.PatientStatus>
                      {patient.situation_label}
                    </Styled.PatientStatus>
                  )}
                </Styled.PatientDetails>
              </Styled.PatientInfo>

              <Styled.MenuButton onClick={(e) => handleMenuClick(e, patient)}>
                <MenuIcon />
              </Styled.MenuButton>
            </Styled.PatientCard>
          ))}
        </Styled.ResultsList>
      ) : hasSearched && searchTerm.length >= 3 ? (
        <Styled.EmptyState>
          <FaUser size={48} />
          <p>Nenhum paciente encontrado.</p>
          <Typography variant="body2" color="textSecondary">
            Verifique os dados e tente novamente.
          </Typography>
        </Styled.EmptyState>
      ) : (
        <Styled.EmptyState>
          <FaMagnifyingGlass size={48} />
          <p>Digite pelo menos 3 caracteres para buscar.</p>
        </Styled.EmptyState>
      )}

      {/* Menu de opções */}
      <Menu
        id="patient-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "patient-menu-button",
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenPatientInfo(true);
            setAnchorEl(null);
          }}
          sx={{ fontSize: "17px", padding: "10px", display: "flex", alignItems: "center", gap: "10px" }}
        >
          <FaCircleInfo color="#053779" /> Informações do Paciente
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/documentation/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Documentação
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/odontograma-user/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Odontograma
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/clinical-record-user/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Ficha Clínica
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/avaliation-user/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Avaliação
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/clinical-budgets/${selectedPatient.pac_id}`, { state: { pac_name: selectedPatient.pac_name, pac_id: selectedPatient.pac_id } });
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px", display: "flex", alignItems: "center", gap: "10px" }}
        >
          <IoReceiptOutline color="#10b981" /> Orçamentos Clínicos
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/notes/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Anotações (Conexão)
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/evolution/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Evolução
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedPatient) {
              navigate(`/record-physical/${selectedPatient.pac_id}`);
            }
            handleMenuClose();
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Ficha Clínica Antiga
        </MenuItem>

        <MenuItem
          onClick={() => {
            setOpenDentistHistory(true);
            setAnchorEl(null);
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Histórico de Dentistas
        </MenuItem>

        <MenuItem
          onClick={() => {
            setOpenReferrals(true);
            setAnchorEl(null);
          }}
          sx={{ fontSize: "17px", padding: "10px" }}
        >
          Encaminhamentos
        </MenuItem>
      </Menu>

      {/* Modal de Histórico de Dentistas */}
      {selectedPatient && (
        <DentistHistoryModal
          open={openDentistHistory}
          onClose={() => {
            setOpenDentistHistory(false);
            setSelectedPatient(null);
          }}
          patientId={selectedPatient.pac_id}
          patientName={selectedPatient.pac_name}
        />
      )}

      {/* Modal de Encaminhamentos */}
      {selectedPatient && (
        <Modal
          open={openReferrals}
          onClose={() => {
            setOpenReferrals(false);
            setSelectedPatient(null);
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95%",
              maxWidth: "900px",
              maxHeight: "90vh",
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ReferralStatusTracker
              pacienteId={selectedPatient.pac_id}
              onClose={() => {
                setOpenReferrals(false);
                setSelectedPatient(null);
              }}
            />
          </Box>
        </Modal>
      )}

      {/* Modal de Informações do Paciente */}
      <PatientInfoModal
        open={openPatientInfo}
        onClose={() => {
          setOpenPatientInfo(false);
          setSelectedPatient(null);
        }}
        patientId={selectedPatient?.pac_id || 0}
      />
    </Styled.Container>
  );
};

export { PatientSearch };


